import React from "react";
import moment from "moment";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import { serverFormat } from "../../../utils/dates";
import OutboundActions from "./OutboundActions";
import CardExpandable from "../../card/CardExpandable";
import DetailField from "../../card/DetailField";
import OrderStatusChip from "../orders/OrderStatusChip";
import { movewareJobId } from "src/components/integrations/moveware/movewareJobDisplay";

export default function OutboundRowMobile({
  item,
  onRemove,
  refetch,
  showStartDate = false,
  showDeliveryDate = false,
  showOrderComments = false,
  showProjectName = false,
  showOutboundWeight = false,
  showBillingCharge = false,
  showMovewareDetails = false,
}) {
  const orderNumber = `${item.workOrderNumber}${
    item.revisionNumber && item.revisionNumber !== "0" ? `.${item.revisionNumber}` : ""
  }`;

  const submittedOnDate = moment(item.requestedAt, serverFormat).format("ll");
  const submittedOnTime = moment(item.requestedAt, serverFormat).format("h:mm A");
  const submittedBy = item.user.email;
  const formatOutboundWeight = outboundWeight => {
    if (outboundWeight == null || outboundWeight === "") return "-";

    return `${Number(outboundWeight).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })} lbs`;
  };

  const billingChargeTotal =
    item.billingCharges?.grand_total ??
    item.billingCharges?.grandTotal ??
    item.outboundCharges?.total;

  const formatBillingCharge = total => {
    if (total == null || total === "") return "-";

    return Number(total).toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
    });
  };

  return (
    <Card>
      <CardContent sx={{ "&:last-child": { pb: 2 } }}>
        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography color="text.secondary" fontWeight={500}>
              {orderNumber}
            </Typography>
            {showProjectName && (
              <Typography color="text.secondary" fontWeight={500}>
                {item.projectName}
              </Typography>
            )}
            <Chip
              label={submittedOnDate}
              size="small"
              sx={{ borderRadius: 1, backgroundColor: "rgba(0, 0, 0, 0.05)" }}
            />
          </Box>
          <Typography mt={1} color="primary" sx={{ wordBreak: "break-word" }}>
            {submittedBy}
          </Typography>
        </Box>
        <CardExpandable
          actions={<OutboundActions item={item} onRemove={onRemove} refetch={refetch} />}
        >
          <Box display="flex" flexDirection="column" gap={1}>
            {showProjectName && <DetailField label="Project Name" value={item.projectName || "-"} />}
            <DetailField label="Submitted On">
              <Box display="flex" flexDirection="column">
                <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                  {submittedOnDate}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  {submittedOnTime}
                </Typography>
              </Box>
            </DetailField>
            {showMovewareDetails && (
              <DetailField label="Moveware Details" value={`Job ID: ${movewareJobId(item) || "—"}`} />
            )}
            {showStartDate && item.startDate && (
              <DetailField
                label="Start Date"
                value={moment(item.startDate, serverFormat).format("ll")}
              />
            )}
            {showDeliveryDate && item.deliveryDate && (
              <DetailField
                label="Delivery Date"
              >
                <Box display="flex" flexDirection="column" gap={0.75}>
                  <Typography variant="body2">
                    {moment(item.deliveryDate, serverFormat).format("ll")}
                  </Typography>
                  <Box>
                    <OrderStatusChip vaStatus={item.vaStatus} />
                  </Box>
                </Box>
              </DetailField>
            )}
            {showOutboundWeight && (
              <DetailField
                label="Outbound Weight"
                value={formatOutboundWeight(item.effectiveOutboundWeight || item.outboundWeight)}
              />
            )}
            {showBillingCharge && (
              <DetailField label="Billing Charge" value={formatBillingCharge(billingChargeTotal)} />
            )}
            {showOrderComments && item.comment && (
              <DetailField label="Comments" value={item.comment} />
            )}
          </Box>
        </CardExpandable>
      </CardContent>
    </Card>
  );
}
